import { Doc } from '@dadohr/shared/domainTypes/document';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  sortUserImportDataSources,
  StandardUserField,
  UserImportDataSource,
  UserImportDataSourceAirtable,
  UserImportDataSourceGoogleSheet,
  UserImportDataSourceMSExcel,
  UserImportDataSourceEmailCsv,
  UserImportDataSourceSftp,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import { ExternalLink, GripVertical, LoaderCircle, SquarePen, X } from 'lucide-react';
import { createElement, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { ColoredChip } from '../../../../../components/ColoredChip';
import { DeleteButton } from '../../../../../components/DeletionConfirmation';
import { Loader } from '../../../../../components/Loader';
import { NoAccess } from '../../../../../components/NoAccess';
import { VisuallyHiddenInputLabel } from '../../../../../components/VisuallyHiddenLabel';
import { useUnsafeTranslation } from '../../../../../i18next';
import { FlexContainer, FlexItem } from '../../../../../layout/Flex';
import { PageBody } from '../../../../../layout/PageBody';
import {
  PageToolbar,
  PageToolbarTitle
} from '../../../../../layout/PageToolbar';
import { useRoutes } from '../../../../../routes';
import { useCurrentUser } from '../../../../../services/currentUser';
import { removeDoc, updateDoc } from '../../../../../services/firebase';
import { hasPermissionToAccessPage } from '../../../../../services/permissions';
import { useUserFields } from '../../../../../services/user';
import { updateImportDataSourceInactiveStatus, updateImportDataSourcesOrder, useUserImportDataSources } from '../../../../../services/user/import';
import { useTheme } from '../../../../../themes';
import { getAdpWorkforceNowFields } from '../../../../Org/pages/Integrations/integrations/adp/service';
import { getAshbyFields } from '../../../../Org/pages/Integrations/integrations/ashby/service';
import { getChartHopFields } from '../../../../Org/pages/Integrations/integrations/charthop/service';
import { getGreenhouseV3Fields } from '../../../../Org/pages/Integrations/integrations/greenhouseV3/service';
import { getPersonioImportFields } from '../../../../Org/pages/Integrations/integrations/personio/service';
import { getPingboardFields } from '../../../../Org/pages/Integrations/integrations/pingboard/service';
import { getUkgFields } from '../../../../Org/pages/Integrations/integrations/ukg/service';
import { useUsersSideNav } from '../../sideNav';
import {
  AutoCompleteMappingFormContainer,
  MergeImportMappingFormContainer
} from '../components/CustomImportMappingForm';
import { EmptyState } from '../components/EmptyState';
import { AddImportSourceButton } from '../components/ImportIntegrationButton';
import { createAirtableViewButton } from '../components/sources/airtable';
import { ImportMappingForm } from '../components/sources/common';
import { MergeInfoPanel } from '../components/sources/mergeHRIS';
import {
  ImportSourceIntegrationInfo,
  IntegrationsInfosMap,
  MergeHRISIntegrationsInfoMap
} from '../components/sources/meta';
import { EmailCsvInfoBox } from '../components/sources/emailCsv';
import { SftpCredentialsInfoBox } from '../components/sources/sftp';
import {
  createExcelViewButton,
  createGoogleSheetViewButton
} from '../components/sources/spreadsheet';

type LoadingState = { 
  type: "loading" 
};

type ErrorState = { 
  type: "error";
  error: any 
};

type ViewingState = { 
  type: "viewing"; 
  sources: Doc<UserImportDataSource>[]
};

type ReorderingState = { 
  type: "reordering"; 
  sources: Doc<UserImportDataSource>[];
  localSources: Doc<UserImportDataSource>[];
  savingItemIds: Set<string>;
  sourceIndex: number;
  destinationIndex: number;
};

type SourcesState = 
  | LoadingState 
  | ErrorState 
  | ViewingState 
  | ReorderingState;

const mergeHrisInfos = Object.values(MergeHRISIntegrationsInfoMap);
const staticInfos = Object.values(IntegrationsInfosMap);

const IMPORT_INTEGRATIONS: ImportSourceIntegrationInfo[] = mergeHrisInfos
  .concat(staticInfos);

const createViewButton = (
  d: Doc<UserImportDataSource>
): {
  href: string;
  disabled?: boolean;
} | null => {
  switch (d.data.type) {
    case UserImportSourceType.AIRTABLE:
      return createAirtableViewButton(d as Doc<UserImportDataSourceAirtable>);

    case UserImportSourceType.GOOGLE_SHEET:
      return createGoogleSheetViewButton(
        d as Doc<UserImportDataSourceGoogleSheet>
      );
    case UserImportSourceType.MS_EXCEL:
      return createExcelViewButton(d as Doc<UserImportDataSourceMSExcel>);

    default:
      return null;
  }
};

const AccordionDetailsBlock = styled(AccordionDetails)`
  flex-direction: column;
  gap: ${(p) => p.theme.spacing(2)}px;
`;

const AccordionWithBorder = styled(Accordion)`
  margin: 0 !important;

  :not(:first-of-type) {
    border-top: 1px solid ${(p) => p.theme.custom.colors.gray.w200};
  }

  ::before {
    opacity: 0 !important;
  }

  .MuiAccordionSummary-root {
    padding-inline: ${(p) => p.theme.spacing(1)}px;
  }
`;

const DragHandle = styled.div`
  cursor: grab;
  display: flex;
  align-items: center;
  margin-right: ${(p) => p.theme.spacing(1)}px;
  color: ${(p) => p.theme.custom.colors.gray.w400};
  
  &:active {
    cursor: grabbing;
  }
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${(p) => p.theme.spacing(1)}px;
  color: ${(p) => p.theme.palette.primary.main};
  animation: spin 1.5s linear infinite;
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ImportSourceNameFieldAndTitle = ({
  mode,
  name,
  onCancel,
  onSave
}: {
  mode: 'edit' | 'show';
  name: string;
  onCancel: () => any;
  onSave: (name: string) => any;
}) => {
  const { t } = useUnsafeTranslation();
  const [state, setState] = useState(name);

  return (
    <>
      {mode === 'show' && (
        <Typography component="h2" variant="h5">
          {name}
        </Typography>
      )}

      {mode === 'edit' && (
        <FlexContainer fullWidth>
          <VisuallyHiddenInputLabel>
            {t('Source name')}
          </VisuallyHiddenInputLabel>
          <TextField
            value={state}
            onChange={(event) => setState(event.target.value)}
            style={{ minWidth: '25ch' }}
          />

          <Button
            color="primary"
            variant="contained"
            onClick={() => onSave(state)}
          >
            Save
          </Button>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
        </FlexContainer>
      )}
    </>
  );
};

// enum IntegrationNameSource {
//   dado,
//   external,
//   none
// }

// const INTEGRATION_NAME_SOURCES: {
//   [k in UserImportSourceType]?: IntegrationNameSource;
// } = {
//   [UserImportSourceType.AIRTABLE]: IntegrationNameSource.dado,
//   [UserImportSourceType.BAMBOO_HR]: IntegrationNameSource.none,
//   [UserImportSourceType.BOB]: IntegrationNameSource.none,
//   [UserImportSourceType.CHARTHOP]: IntegrationNameSource.none,
//   [UserImportSourceType.GOOGLE_SHEET]: IntegrationNameSource.external,
//   [UserImportSourceType.WORKDAY_REPORT_API]: IntegrationNameSource.dado,
//   [UserImportSourceType.MERGE_HRIS]: IntegrationNameSource.none,
//   [UserImportSourceType.SFTP]: IntegrationNameSource.dado
// };

const ADDITIONAL_FIELD_MAPPERS: {
  [k in UserImportSourceType]?: {
    getFields: (orgId: string) => Promise<{ name: string; label: string }[]>;
  };
} = {
  [UserImportSourceType.CHARTHOP]: {
    getFields: (orgId) => getChartHopFields(orgId)
  },
  [UserImportSourceType.GREENHOUSE_V3]: {
    getFields: (orgId) => getGreenhouseV3Fields(orgId)
  },
  [UserImportSourceType.PERSONIO]: {
    getFields: (orgId) => getPersonioImportFields(orgId)
  },
  [UserImportSourceType.PINGBOARD]: {
    getFields: (orgId) =>
      getPingboardFields(orgId).then((fs) =>
        fs.map((f) => ({
          name: f.fieldPath,
          label: f.label
        }))
      )
  },
  [UserImportSourceType.ASHBY]: {
    getFields: (orgId) =>
      getAshbyFields(orgId).then((fs) =>
        fs.map((f) => ({
          name: f.path,
          label: f.label
        }))
      )
  },
  [UserImportSourceType.ADP_WORKFORCE_NOW]: {
    getFields: (orgId) =>
      getAdpWorkforceNowFields(orgId).then((fs) =>
        fs.map((f) => ({
          name: f.fieldPath,
          label: f.label
        }))
      )
  },
  [UserImportSourceType.UKG]: {
    getFields: (orgId) =>
      getUkgFields(orgId).then((fs) =>
        fs.map((f) => ({
          name: f.fieldPath,
          label: f.label
        }))
      )
  }
};

const INFO_COMPONENTS: {
  [k in UserImportSourceType]?: React.ComponentType<{
    d: Doc<UserImportDataSource>;
  }>;
} = {
  [UserImportSourceType.SFTP]: ({ d }) => {
    return <SftpCredentialsInfoBox d={d as Doc<UserImportDataSourceSftp>} />;
  },
  [UserImportSourceType.EMAIL_CSV]: ({ d }) => {
    return (
      <EmailCsvInfoBox d={d as Doc<UserImportDataSourceEmailCsv>} />
    );
  }
};

const ImportSource = ({
  d,
  userFields,
  index,
  isSaving,
  onDelete,
}: {
  d: Doc<UserImportDataSource>;
  userFields: (StandardUserField | CustomUserField)[];
  index: number;
  isSaving?: boolean;
  onDelete?: () => Promise<any>;
  onNameChange?: () => any;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useUnsafeTranslation();
  const theme = useTheme();

  const [isRenaming, setIsRenaming] = useState(false);
  const viewButton = createViewButton(d);

  const fieldTerm =
    d.data.type === UserImportSourceType.GOOGLE_SHEET ||
    d.data.type === UserImportSourceType.MS_EXCEL ||
    d.data.type === UserImportSourceType.SFTP ||
    d.data.type === UserImportSourceType.EMAIL_CSV
      ? t('column')
      : t('field');

  const integrationInfo = IMPORT_INTEGRATIONS.find((m) => {
    if (d.data.type === UserImportSourceType.MERGE_HRIS) {
      return (
        m.type === UserImportSourceType.MERGE_HRIS &&
        m.mergeHRIS === d.data.hris
      );
    }

    return m.type === d.data.type;
  });

  const sourceName =
    d.data.type === UserImportSourceType.AIRTABLE ||
    d.data.type === UserImportSourceType.GOOGLE_SHEET ||
    d.data.type === UserImportSourceType.MS_EXCEL ||
    d.data.type === UserImportSourceType.WORKDAY_REPORT_API ||
    d.data.type === UserImportSourceType.SFTP ||
    d.data.type === UserImportSourceType.EMAIL_CSV
      ? d.data.name
      : null;

  const toggleIsExpanded = () => !isRenaming && setIsExpanded(!isExpanded);

  if (!integrationInfo) {
    console.warn(`Integration info for ${d.data.type} is not defined`);
    return null;
  }

  const isMergeDataSource = d.data.type === UserImportSourceType.MERGE_HRIS;

  const additionalFieldMapper = ADDITIONAL_FIELD_MAPPERS[d.data.type] || null;

  const infoComponent = INFO_COMPONENTS[d.data.type] || null;

  return (
    <Draggable draggableId={d.id} index={index} isDragDisabled={isSaving}>
      {(provided) => (
        <AccordionWithBorder elevation={0} expanded={isExpanded} ref={provided.innerRef} {...provided.draggableProps}>
          <AccordionSummary onClick={toggleIsExpanded}>
            <Grid container>
              <FlexItem flex={1}>
                <FlexContainer style={{ marginInlineEnd: theme.spacing(2) }}>
                  {isSaving ? (
                      <LoadingIndicator>
                        <LoaderCircle size={20} />
                      </LoadingIndicator>
                    ) : (
                      <DragHandle {...provided.dragHandleProps}>
                        <GripVertical size={20} />
                      </DragHandle>
                    )
                  }

                  <img
                    src={integrationInfo.smallLogoUrl}
                    width={24}
                    alt={integrationInfo.name}
                    style={{opacity: d.data.isInactive ? 0.5 : 1}}
                  />

                  <ImportSourceNameFieldAndTitle
                    mode={isRenaming ? 'edit' : 'show'}
                    name={sourceName || integrationInfo?.name}
                    onCancel={() => setIsRenaming(false)}
                    onSave={(name) => {
                      setIsRenaming(false);
                      updateDoc(d, () => ({ name }));
                    }}
                  />
                  <ColoredChip color={d.data.isInactive ? 'gray' : 'green'} size="small">{ d.data.isInactive ? 'Inactive' : 'Active' }</ColoredChip>
                </FlexContainer>
              </FlexItem>
              <FlexContainer spacing={2} onClick={(ev) => ev.stopPropagation()}>
                {viewButton && (
                  <Button
                    size="small"
                    disabled={viewButton.disabled}
                    href={viewButton.href}
                    rel="noopener"
                    target="_blank"
                    color="primary"
                    startIcon={<ExternalLink size={14} />}
                  >
                    {t('Open')}
                  </Button>
                )}
                
                <Button
                  size="small"
                  color="primary"
                  startIcon={isExpanded ? <X size={14} /> : <SquarePen size={14} />}
                  onClick={(e) => toggleIsExpanded()}
                >
                  {isExpanded ? t('Close Settings') : t('Edit Settings')}
                </Button>
              </FlexContainer>
            </Grid>
          </AccordionSummary>
          <AccordionDetailsBlock>
            {infoComponent && createElement(infoComponent, { d })}
            <div>
              <Grid>
                <FlexContainer justifyContent='space-between' spacing={2}>
                  <FormControlLabel
                    label={
                      !d.data.isInactive ? (
                        <span>
                          <strong>Active:</strong> data will be synced every import
                        </span>
                      ) : (
                        'Active'
                      )
                    }
                    control={
                      <Switch
                        checked={!d.data.isInactive}
                        onChange={() => {
                          updateImportDataSourceInactiveStatus(
                            d, 
                            !d.data.isInactive,
                            { reason: "User toggled status via settings panel" }
                          );
                        }}
                      />
                    }
                  />

                  {onDelete && (
                    <DeleteButton
                      buttonAction="Remove"
                      variant="outlined"
                      color="secondary"
                      dialogTitle={t('Remove {{sourceName}} as import source', {
                        sourceName
                      })}
                      onDelete={onDelete}
                    >
                      {t('Remove Import Source')}
                    </DeleteButton>
                  )}
                </FlexContainer>
              </Grid>
              {!isMergeDataSource && !additionalFieldMapper && (
                <ImportMappingForm
                  key={d.id}
                  fieldTerm={fieldTerm}
                  userFields={userFields}
                  d={d}
                  integrationName={integrationInfo.name}
                />
              )}

              {additionalFieldMapper && (
                <AutoCompleteMappingFormContainer
                  importDataSourceDocument={d}
                  integrationInfo={integrationInfo}
                  userFields={userFields}
                  getFields={() => additionalFieldMapper.getFields(d.data.orgId)}
                />
              )}

              {isMergeDataSource && (
                <>
                  <MergeInfoPanel name={integrationInfo.name} />
                  <MergeImportMappingFormContainer
                    importDataSourceDocument={d}
                    integrationInfo={integrationInfo}
                    userFields={userFields}
                  />
                </>
              )}
            </div>
          </AccordionDetailsBlock>
        </AccordionWithBorder>
      )}
    </Draggable>
  );
};

const ImportSourcesList = ({
  ds,
  userFields,
  onReorder,
  savingItemIds
}: {
  ds: Doc<UserImportDataSource>[];
  userFields: (StandardUserField | CustomUserField)[];
  onReorder: (sourceIndex: number, destinationIndex: number) => any,
  savingItemIds: Set<string>
}) => {
  const handleDragEnd = (result: any) => {
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }
    
    onReorder(result.source.index, result.destination.index);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="import-sources-list">
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {sortUserImportDataSources(ds).map((d, i) => (
              <ImportSource
                d={d}
                key={`i-${d.data.lastUpdate?.toMillis() || d.id}`}
                index={i}
                userFields={userFields}
                onDelete={() => removeDoc(d)}
                isSaving={savingItemIds?.has(d.id)}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

const ContentUserImportSources = () => {
  const { org } = useCurrentUser();
  const { t } = useUnsafeTranslation();
  const sideNav = useUsersSideNav();

  const [state, setState] = useState<SourcesState>({ type: "loading" });
  const userFields = useUserFields(org.customUserFields);

  const [rawSources, isLoading, error] = useUserImportDataSources(org.id);

  useEffect(() => {
    if (isLoading) {
      setState({ type: "loading" });
      return;
    }
    
    if (error) {
      setState({ type: "error", error });
      return;
    }

    if (rawSources) {
      setState((currentState) => {
        if (currentState.type === "reordering") {
          return currentState;
        } else {
          return { 
            type: "viewing", 
            sources: rawSources 
          };
        }
      });
    }
  }, [rawSources, isLoading, error]);

  if (!rawSources) {
    return null;
  }

  const handleReorder = async (sourceIndex: number, destinationIndex: number) => {
    if (state.type !== "viewing") return;

    const sortedSources = sortUserImportDataSources(rawSources);

    const reorderedSources = [...sortedSources];
    const [movedSource] = reorderedSources.splice(sourceIndex, 1);
    reorderedSources.splice(destinationIndex, 0, movedSource);

    const updatedSources = reorderedSources.map((source, index) => ({
      ...source,
      data: {
        ...source.data,
        importOrder: index
      }
    }));

    const itemsToUpdate = updatedSources
      .filter((source, index) => {
        const originalSource = sortedSources.find(s => s.id === source.id);
        return originalSource?.data.importOrder !== index;
      })
      .map(source => source.id);

    setState({
      type: "reordering",
      sources: state.sources,
      localSources: updatedSources,
      savingItemIds: new Set(itemsToUpdate),
      sourceIndex,
      destinationIndex
    });

    try {
      await updateImportDataSourcesOrder(sortedSources, sourceIndex, destinationIndex, {reason: 'User reordered imports from settings panel'});

      setState(currentState => {
        if (currentState.type === "reordering") {
          return {
            type: "viewing",
            sources: updatedSources
          };
        }
        return currentState;
      });
    } catch (error) {
      console.error('Error updating import source order:', error);
      setState({
        type: "viewing",
        sources: rawSources
      });
    }
  };

  const renderContent = () => {
    switch (state.type) {
      case "loading":
        return <Loader height={500} />;
        
      case "error":
        return <div>Error loading import sources: {state.error.message}</div>;
        
      case "viewing":
        return state.sources.length > 0 ? (
          <ImportSourcesList 
            ds={sortUserImportDataSources(state.sources)} 
            userFields={userFields} 
            onReorder={handleReorder}
            savingItemIds={new Set()}
          />
        ) : (
          <EmptyState
            title={t('No import sources')}
            instruction={t('Add a new source to enable importing users')}
          />
        );
        
      case "reordering":
        return (
          <ImportSourcesList 
            ds={sortUserImportDataSources(state.localSources)} 
            userFields={userFields} 
            onReorder={() => {}}
            savingItemIds={state.savingItemIds}
          />
        );
    }
  };

  return (
    <PageBody noTopPadding sideNav={sideNav}>
      <PageToolbar sticky>
        <PageToolbarTitle>
          <FlexContainer fullWidth justifyContent="space-between">
            <Typography component="h1" variant="h1">
              {t('Import Sources')}
            </Typography>

            <AddImportSourceButton org={org} />
          </FlexContainer>
        </PageToolbarTitle>
      </PageToolbar>

      {renderContent()}
    </PageBody>
  );
};

export const PageUserImportSources = () => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();

  if (
    !hasPermissionToAccessPage(currentUser, ROUTES.users.imports.importSources)
  ) {
    return <NoAccess />;
  }

  return <ContentUserImportSources />;
};
